<template>
  <div class="radio_list" :style="radioListStyle">
    <div
      v-for="(option, index) in computedOptions"
      :key="`${_uid}_option_${index}_${option.displayValue}}`"
      class="radio_list__option"
      :tabindex="disabled || option.disabled ? -1 : 0"
      :class="{
        'radio_list__option--selected': option.isActive,
        'radio_list__option--block': isBlock,
        'radio_list__option--disabled': disabled || option.disabled,
      }"
      @click="select(option)"
      @keydown.enter.prevent="select(option)"
    >
      <div
        class="radio_list__knob"
        :class="{ 'radio_list__knob--selected': option.isActive }"
      ></div>
      <span
        v-if="!option.isComponent"
        :class="{ 'option__label--disabled': disabled || option.disabled }"
      >
        {{ option.displayValue }}
      </span>
      <slot v-else :name="option.name">{{ option.name }}</slot>
    </div>
    <ClearButton @clear="clearValue" v-if="!required && value && !disabled" />
    <ValidationMessage
      :displayInvalid="displayInvalid"
      :validation-message="externalValidationErrorMessage || validationMessage"
    />
  </div>
</template>

<script>
import BaseComponent from "../../BaseComponent";
import InputBase from "./mixins/InputBase";
import ClearButton from "../ClearButton.vue";

/**
 * @group Inputs
 * Displays a list of options out of which the user can only select one.
 */
export default {
  mixins: [BaseComponent, InputBase],
  name: "RadioList",
  components: {
    ClearButton,
  },
  props: {
    // Option list
    options: {
      type: Array,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    validationMessage: {
      type: String,
      default: "Please select an option",
    },
    // Display option. When true every option is displayed as a block.
    isBlock: {
      type: Boolean,
      default: false,
    },
    // Display option. Defines over how much columns the option are spread.
    columns: {
      type: Number,
      default: 1,
    },
    enableExternalValidation: {
      type: Boolean,
      default: false,
    },
    externalValidationError: {
      type: Boolean,
    },
    externalValidationErrorMessage: {
      type: String,
    },
  },
  data() {
    return {
      selectedOptionIsComponent: false,
      selectedComponentOptionName: "",
    };
  },
  computed: {
    displayInvalid() {
      if (this.enableExternalValidation) return this.externalValidationError;
      return this.isShowingValidationMessage && !this.isValid;
    },
    isValid() {
      if (!this.required) return true;
      return !!this.inputValue;
    },
    radioListStyle() {
      let columnTemplate = "";
      for (let index = 0; index < this.columns; index += 1) {
        columnTemplate += "1fr ";
      }
      return {
        "grid-template-columns": columnTemplate,
      };
    },
    computedOptions() {
      return this.options.map((option) => {
        let isActive =
          JSON.stringify(this.value) === JSON.stringify(option.value);
        if (option.isComponent && this.selectedOptionIsComponent) {
          isActive = this.selectedComponentOptionName === option.name;
        }
        return {
          ...option,
          isActive,
        };
      });
    },
  },
  methods: {
    select(option) {
      if (this.disabled || option.disabled) return;
      this.inputValue = option.value;
      if (!option.isComponent) {
        this.selectedOptionIsComponent = false;
        this.selectedComponentOptionName = "";
      } else {
        this.selectedOptionIsComponent = true;
        this.selectedComponentOptionName = option.name;
      }
    },
    // @vuese
    clearValue() {
      if (this.disabled) return;
      this.inputValue = "";
    },
  },
};

// Documentation Prop Exports
const documentationProps = [
  {
    options: [
      { displayValue: "Centimetre", value: "centimetre" },
      { displayValue: "Decimetre", value: "decimetre" },
      { displayValue: "Metre", value: "metre" },
      {
        value: "other",
        name: "nameBecomesTheSlotName",
        isComponent: true,
      },
    ],
    required: true,
    isBlock: false,
    columns: 1,
  },
];

export { documentationProps };
</script>

<style scoped lang="scss">
@import "../../../style_variables/style_variables.scss";

.radio_list {
  width: 100%;
  display: inline-grid;
  grid-column-gap: 8px;
  max-width: $input_max_width;
}
.radio_list__option {
  @extend %body2_style;
  display: inline-flex;
  align-items: center;
  max-width: $input_max_width;
  min-height: 40px;
  padding-right: 20px;
  &:hover {
    cursor: pointer;
    .radio_list__knob {
      background-color: $grey_athens;
    }
  }
  &.radio_list__option--selected {
    .radio_list__knob {
      &:after {
        content: "";
        display: inline-block;
        width: 12px;
        height: 12px;
        background-color: $blue;
        border-radius: 6px;
      }
    }
  }
  &.radio_list__option--block {
    width: 100%;
    height: 56px;
    border: 1px solid $grey_french;
    background-color: $white;
    padding: 16px;
    margin-bottom: 8px;
    border-radius: 4px;
    &:hover {
      border-color: $grey_french;
    }
    &.radio_list__option--selected {
      border-color: $blue;
    }
  }
  &.radio_list__option--disabled {
    border-color: $grey_mischka;
    outline: none;
    .radio_list__knob {
      background-color: $grey_bg;
      border-color: $grey_mischka;
      &:after {
        background-color: $grey_mischka;
      }
    }
    &:hover {
      cursor: default;
      .radio_list__knob {
        background-color: $grey_bg;
      }
    }
    &.radio_list__option--selected {
      border-color: $grey_mischka;
    }
  }
}
.radio_list__knob {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 12px;
  background-color: $white;
  border: 2px solid $grey_mischka;
  border-radius: 10px;
  flex-shrink: 0;

  &.radio_list__knob--selected {
    border-color: $blue;
  }
}
.option__label--disabled {
  color: $grey_mischka;
}
</style>
